import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import LocomotiveScroll from 'locomotive-scroll';
import 'locomotive-scroll/dist/locomotive-scroll.css';

gsap.registerPlugin(ScrollTrigger);

/**
 * ============================================
 * Modatex Home Page - Bélanger Style
 * ============================================
 * Premium textile showcase with scroll animations
 */

const Home = () => {
    // Loading state
    const [loading, setLoading] = useState(true);
    const [progress, setProgress] = useState(0);

    // Refs for animations
    const containerRef = useRef(null);
    const scrollRef = useRef(null);
    const heroRef = useRef(null);
    const horizontalRef = useRef(null);
    const section2Ref = useRef(null);
    const section3Ref = useRef(null);
    const section4Ref = useRef(null);
    const section5Ref = useRef(null);
    const locoScrollRef = useRef(null);

    // Loading screen animation
    useEffect(() => {
        // Simulate loading progress
        const interval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    clearInterval(interval);
                    setTimeout(() => setLoading(false), 500);
                    return 100;
                }
                return prev + 10;
            });
        }, 150);

        return () => clearInterval(interval);
    }, []);

    // GSAP Animations
    useEffect(() => {
        // Don't initialize if still loading
        if (loading) return;

        // Wait for DOM to be ready
        const timer = setTimeout(() => {
            if (!scrollRef.current || !containerRef.current || !heroRef.current) {
                console.warn('Refs not ready yet');
                return;
            }

            const locoScroll = new LocomotiveScroll({
            el: scrollRef.current,
            smooth: true,
            smoothMobile: true,
            multiplier: 0.8,
            lerp: 0.08,
        });

        // Store in ref for access in event listeners
        locoScrollRef.current = locoScroll;

        // Integrate Locomotive Scroll with GSAP ScrollTrigger
        locoScroll.on('scroll', ScrollTrigger.update);

        ScrollTrigger.scrollerProxy(scrollRef.current, {
            scrollTop(value) {
                return arguments.length
                    ? locoScroll.scrollTo(value, 0, 0)
                    : locoScroll.scroll.instance.scroll.y;
            },
            getBoundingClientRect() {
                return {
                    top: 0,
                    left: 0,
                    width: window.innerWidth,
                    height: window.innerHeight,
                };
            },
            pinType: scrollRef.current.style.transform ? 'transform' : 'fixed',
        });

        const ctx = gsap.context(() => {
            // Horizontal scroll for hero section
            const horizontalSection = horizontalRef.current;
            const scrollWidth = horizontalSection.scrollWidth - window.innerWidth;

            gsap.to(horizontalSection, {
                x: -scrollWidth,
                ease: 'none',
                scrollTrigger: {
                    trigger: heroRef.current,
                    start: 'top top',
                    end: () => `+=${scrollWidth}`,
                    pin: true,
                    scrub: 1,
                    anticipatePin: 1,
                    scroller: scrollRef.current,
                }
            });

            // Section 2 - Fade in from bottom with scrub
            gsap.from(section2Ref.current, {
                scrollTrigger: {
                    trigger: section2Ref.current,
                    start: 'top 80%',
                    end: 'top 30%',
                    scrub: 0.5,
                    scroller: scrollRef.current,
                },
                opacity: 0,
                y: 100,
                ease: 'power2.out'
            });

            // Section 3 - Product showcase with scrub
            gsap.from(section3Ref.current, {
                scrollTrigger: {
                    trigger: section3Ref.current,
                    start: 'top 80%',
                    end: 'top 0%',
                    scrub: 0.5,
                    scroller: scrollRef.current,
                },
                opacity: 0,
                y: 0,
                ease: 'power2.out'
            });

            // Section 4 - Slides up over section 3
            ScrollTrigger.create({
                trigger: section4Ref.current,
                start: 'top bottom',
                end: 'top top',
                pin: section3Ref.current,
                pinSpacing: false,
                scrub: 0.5,
                scroller: scrollRef.current,
            });

            // Section 5 - Slides up over section 4
            ScrollTrigger.create({
                trigger: section5Ref.current,
                start: 'top bottom',
                end: 'top top',
                pin: section4Ref.current,
                pinSpacing: false,
                scrub: 0.5,
                scroller: scrollRef.current,
            });

        }, containerRef);

        // Wait for Locomotive Scroll to be fully initialized before refreshing
        setTimeout(() => {
            if (locoScrollRef.current) {
                locoScrollRef.current.update();
                ScrollTrigger.refresh();
            }
        }, 500);

            return () => {
                ctx.revert();
                if (locoScrollRef.current) {
                    locoScrollRef.current.destroy();
                    locoScrollRef.current = null;
                }
            };
        }, 200);

        return () => clearTimeout(timer);
    }, [loading]);

    // Loading screen
    if (loading) {
        return (
            <div className="fixed inset-0 bg-[#C5C0B8] flex items-center justify-center z-[99999] overflow-hidden">
                {/* Animated background pattern */}
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute top-0 left-0 w-full h-full" 
                         style={{
                             backgroundImage: `repeating-linear-gradient(45deg, #3D3D3D 0, #3D3D3D 1px, transparent 0, transparent 50%)`,
                             backgroundSize: '20px 20px',
                             animation: 'slidePattern 20s linear infinite'
                         }}
                    />
                </div>

                {/* Main content */}
                <div className="relative z-10 text-center">
                    {/* Large Modatex text */}
                    <div className="mb-12">
                        <h1 className="font-serif text-8xl lg:text-9xl text-[#3D3D3D] font-light tracking-tight mb-2"
                            style={{
                                animation: 'fadeInUp 0.8s ease-out'
                            }}>
                            Modatex
                        </h1>
                        <p className="text-sm uppercase tracking-[0.4em] text-[#3D3D3D]/60 font-light"
                           style={{
                               animation: 'fadeIn 1s ease-out 0.3s both'
                           }}>
                            Premium Textiles
                        </p>
                    </div>

                    {/* Elegant progress indicator */}
                    <div className="flex flex-col items-center gap-6"
                         style={{
                             animation: 'fadeIn 1s ease-out 0.5s both'
                         }}>
                        {/* Progress bar */}
                        <div className="relative w-64 h-0.5 bg-[#3D3D3D]/20 overflow-hidden">
                            <div 
                                className="absolute top-0 left-0 h-full bg-[#3D3D3D] transition-all duration-300 ease-out"
                                style={{ width: `${progress}%` }}
                            >
                                {/* Animated shimmer effect */}
                                <div className="absolute top-0 right-0 w-12 h-full bg-gradient-to-r from-transparent via-white/30 to-transparent"
                                     style={{
                                         animation: 'shimmer 1.5s infinite'
                                     }}
                                />
                            </div>
                        </div>

                        {/* Progress percentage */}
                        <div className="flex items-center gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#3D3D3D]"
                                 style={{
                                     animation: 'pulse 1.5s ease-in-out infinite'
                                 }}
                            />
                            <span className="text-xs tracking-[0.2em] text-[#3D3D3D]/70 font-light">
                                {progress}%
                            </span>
                            <div className="w-1.5 h-1.5 rounded-full bg-[#3D3D3D]"
                                 style={{
                                     animation: 'pulse 1.5s ease-in-out infinite 0.5s'
                                 }}
                            />
                        </div>

                        {/* Loading text */}
                        <p className="text-xs text-[#3D3D3D]/50 font-light tracking-wider mt-2">
                            Chargement de l'expérience...
                        </p>
                    </div>
                </div>

                {/* CSS animations */}
                <style>{`
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                        }
                        to {
                            opacity: 1;
                        }
                    }

                    @keyframes shimmer {
                        0% {
                            transform: translateX(-100%);
                        }
                        100% {
                            transform: translateX(400%);
                        }
                    }

                    @keyframes pulse {
                        0%, 100% {
                            opacity: 1;
                            transform: scale(1);
                        }
                        50% {
                            opacity: 0.5;
                            transform: scale(0.8);
                        }
                    }

                    @keyframes slidePattern {
                        0% {
                            transform: translateX(0) translateY(0);
                        }
                        100% {
                            transform: translateX(20px) translateY(20px);
                        }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <>
            {/* ========== NAVBAR ========== */}
            <nav className="fixed top-0 left-0 right-0 z-[9999] bg-transparent pointer-events-none">
                <div className="max-w-[1800px] mx-auto px-6 lg:px-12 pointer-events-auto">
                    <div className="flex items-center justify-between h-20">
                        {/* Logo */}
                        <div className="flex items-center space-x-2">
                            <span className="text-2xl font-serif font-bold text-[#3D3D3D]">Modatex</span>
                        </div>



                        {/* Right side */}
                        <div className="flex items-center space-x-6">
                            <Link 
                                to="/catalog" 
                                className="px-5 py-2.5 bg-[#3D3D3D] text-white text-sm font-medium rounded-full hover:bg-[#2D2D2D] transition-colors"
                            >
                                Nos produits
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            <div ref={scrollRef} data-scroll-container>
                <div ref={containerRef} className="bg-[#C5C0B8]">

            {/* ========== HERO SECTION - Horizontal Scroll ========== */}
            <section ref={heroRef} className="h-screen overflow-hidden relative">
                {/* Mobile Simple Hero */}
                <div className="md:hidden h-full flex flex-col items-center justify-center px-4 bg-[#C5C0B8]">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-3">Textiles Premium</p>
                    <h1 className="font-serif text-4xl text-[#3D3D3D] leading-tight text-center">
                        Lignes, courbes &
                    </h1>
                    <h1 className="font-serif text-4xl text-[#6B6B6B] font-bold mt-1 text-center">
                        MOUVEMENT
                    </h1>
                    
                    {/* Single featured image on mobile */}
                    <div className="mt-12 w-full max-w-sm">
                        <img 
                            src="https://images.unsplash.com/photo-1671328920741-ea6f4233dc07?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                            alt="Premium Curtain"
                            className="w-full h-[40vh] object-cover shadow-2xl"
                        />
                    </div>
                </div>

                {/* Desktop Horizontal Scroll - Hidden on mobile */}
                <div className="hidden md:block h-full">
                    {/* Fixed Center Text - stays in place during horizontal scroll */}
                    <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none px-4">
                        <div className="text-center">
                            <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-4">Textiles Premium</p>
                            <h1 className="font-serif text-5xl lg:text-8xl text-[#3D3D3D] leading-tight">
                                Lignes, courbes &
                            </h1>
                            <h1 className="font-serif text-5xl lg:text-8xl text-[#6B6B6B] font-bold mt-2">
                                MOUVEMENT
                            </h1>
                        </div>
                    </div>

                    {/* Horizontal Scroll Container */}
                    <div 
                        ref={horizontalRef} 
                        className="flex h-full"
                        style={{ width: '200vw' }}
                    >
                        {/* Panel 1 - Initial view with left image, center product, right partial images */}
                        <div className="w-screen h-full shrink-0 relative">
                            {/* Left Image - Tall */}
                            <div className="absolute left-6 lg:left-12 top-1/2 -translate-y-1/2 w-[240px] lg:w-[350px] h-[50%] lg:h-[65%]">
                                <img 
                                    src="https://images.unsplash.com/photo-1671328920741-ea6f4233dc07?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                                    alt="Premium Curtain"
                                    className="w-full h-full object-cover shadow-xl"
                                />
                            </div>
                            
                            {/* Center Product - Below text */}
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-[8%] w-[200px] lg:w-[280px] h-[26%] lg:h-[32%]">
                                <img 
                                    src="https://images.unsplash.com/photo-1609933411981-161f776ad51f?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                                    alt="Textile Detail"
                                    className="w-full h-full object-cover shadow-xl"
                                />
                            </div>
                            
                            {/* Right Top Image - Partial */}
                            <div className="absolute right-0 top-[10%] w-[500px] lg:w-[600px] h-[75%] lg:h-[80%] translate-x-[80%]">
                                <img 
                                    src="https://images.unsplash.com/photo-1696485804806-77f1a3eb63ac?q=80&w=685&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                                    alt="Curtain Interior"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            
                            {/* New Bottom Right Image */}
                            <div className="absolute right-[-65%] lg:right-[-70%] top-[32%] lg:top-[30%] w-[220px] lg:w-[300px] h-[28%] lg:h-[35%]">
                                <img 
                                    src="https://images.unsplash.com/photo-1528822855841-e8bf3134cdc9?q=80&w=735&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                                    alt="Fabric Detail"
                                    className="w-full h-full object-cover shadow-xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== SECTION 2 - Timeless Aesthetics ========== */}
            <section ref={section2Ref} className="min-h-screen bg-[#E8E5DF] flex flex-col items-center justify-center py-20 md:py-32 mb-0 px-4">
                <div className="text-center max-w-5xl mx-auto px-4 md:px-8">
                    <h2 className="font-serif text-[18vw] md:text-[12vw] lg:text-[10rem] text-[#D4D0C8] leading-none">
                        esthétique
                    </h2>
                    <h2 className="font-serif text-[18vw] md:text-[12vw] lg:text-[10rem] text-[#D4D0C8] leading-none -mt-4 md:-mt-8">
                        intemporelle
                    </h2>
                </div>
                
                {/* Product in center */}
                <div className="relative -mt-16 md:-mt-32 z-10 px-4">
                    <img 
                        src="https://plus.unsplash.com/premium_photo-1674567529920-1c424893626b?q=80&w=696&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                        alt="Textile Premium"
                        className="h-[35vh] md:h-[50vh] object-contain"
                    />
                </div>

                <div className="max-w-3xl mx-auto text-center mt-12 md:mt-16 px-6 md:px-8">
                    <p className="text-base md:text-lg text-gray-700 leading-relaxed font-serif italic">
                        Avec des lignes minimalistes et des tons sobres inspirés des tendances du monde entier,
                        nos textiles se distinguent par leur design singulier, leur durabilité et leur qualité parfaite.
                    </p>
                    <p className="mt-4 md:mt-6 text-xs md:text-sm text-gray-600">
                        Beauté et simplicité – c'est la signature unique de Modatex.
                    </p>
                </div>
            </section>

            {/* ========== SECTION 3 - Essential Collection ========== */}
            <section ref={section3Ref} className="h-screen bg-[#2F3D4A] relative overflow-hidden z-10 mt-0">
                <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 md:px-12 lg:px-24">
                    {/* Top Label */}
                    <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] md:tracking-[0.3em] text-pink-300 mb-6 md:mb-8 text-center">Modatex Essentiel</p>
                    
                    {/* Title - Centered */}
                    <div className="text-center mb-8 md:mb-12">
                        <h2 className="font-serif text-3xl md:text-5xl lg:text-7xl text-white leading-tight italic">
                            L'art de se sentir
                        </h2>
                        <h2 className="text-2xl md:text-4xl lg:text-6xl text-white font-bold uppercase mt-2">
                            BIEN CHEZ SOI
                        </h2>
                    </div>

                    {/* Large Center Image */}
                    <div className="relative w-full max-w-xs md:max-w-lg mb-6 md:mb-8">
                        <img 
                            src="https://images.unsplash.com/photo-1617596225496-1d9da33a144b?q=80&w=1171&auto=format&fit=crop"
                            alt="Collection Essentielle"
                            className="w-full h-auto object-cover"
                        />
                    </div>

                    
                    {/* Right Side Text */}
                    <div className="relative md:absolute md:right-12 lg:right-24 md:bottom-[15%] max-w-md px-4 md:px-0">
                        <p className="text-white/90 text-xs md:text-sm leading-relaxed text-center md:text-left">
                            Rassemblant des produits textiles de style et de qualité supérieurs, 
                            notre gamme de produits Essential est la somme de tous ces petits détails 
                            qui subliment le quotidien et embellissent nos espaces de vie.
                        </p>
                        <a href="#" className="inline-flex items-center mt-4 md:mt-6 text-white text-xs md:text-sm hover:underline mx-auto md:mx-0">
                            Explorer la gamme de produits
                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                        </a>
                    </div>
                </div>
            </section>

            {/* ========== SECTION 4 - Pro Collection (Stacks over Section 3) ========== */}
            <section ref={section4Ref} className="h-screen bg-[#4A3D3D] relative overflow-hidden z-20">
                <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 md:px-12 lg:px-24">
                    {/* Top Label */}
                    <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] md:tracking-[0.3em] text-pink-200 mb-6 md:mb-8 text-center">Modatex Pro</p>
                    
                    {/* Title - Centered */}
                    <div className="text-center mb-8 md:mb-12">
                        <h2 className="font-serif text-3xl md:text-5xl lg:text-7xl text-[#D4D0C8] leading-tight italic">
                            Conçu pour les
                        </h2>
                        <h2 className="text-2xl md:text-4xl lg:text-6xl text-white font-bold uppercase mt-2">
                            PROFESSIONNELS
                        </h2>
                    </div>

                    <div className="relative w-full max-w-xs md:max-w-lg mb-6 md:mb-8">
                        <img 
                            src="https://images.unsplash.com/photo-1549648183-6f3ef2c6d21e?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                            alt="Collection Essentielle"
                            className="w-full h-auto object-cover"
                        />
                    </div>
                    
                    {/* Right Side Text */}
                    <div className="relative md:absolute md:right-12 lg:right-24 md:bottom-[15%] max-w-md px-4 md:px-0">
                        <p className="text-white/90 text-xs md:text-sm leading-relaxed text-center md:text-left">
                            La gamme Pro offre des collections élégantes, minimalistes et intelligentes 
                            et est le choix parfait pour tout projet, lieu et décor.
                        </p>
                        <a href="#" className="inline-flex items-center mt-4 md:mt-6 text-white text-xs md:text-sm hover:underline mx-auto md:mx-0">
                            Explorer la gamme de produits
                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                        </a>
                    </div>
                </div>
            </section>

            {/* ========== SECTION 5 - Mission (Stacks over Section 4) ========== */}
            <section ref={section5Ref} className="h-[40vh] bg-[#C5C0B8] flex flex-col items-center justify-center py-12 md:py-16 relative z-30 px-4">
                <div className="text-center max-w-5xl mx-auto px-4 md:px-8">
                    <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] md:tracking-[0.3em] text-pink-600 mb-6 md:mb-8">Notre Mission</p>
                    <h2 className="font-serif text-xl md:text-3xl lg:text-5xl text-[#3D3D3D] leading-relaxed italic">
                        Créer des collections textiles intemporelles,
                        <br />
                        durables et conçues pour embellir
                        <br className="hidden md:block" />
                        le quotidien.
                    </h2>
                </div>
            </section>

            {/* ========== SECTION 6 - Benefits ========== */}
            <section className="min-h-screen bg-white py-20 md:py-32 relative z-40">
                <div className="max-w-7xl mx-auto px-4 md:px-8">
                    <div className="flex flex-col md:flex-row justify-between items-start mb-12 md:mb-16">
                        <div>
                            <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] md:tracking-[0.3em] text-pink-600 mb-3 md:mb-4">Nos Avantages</p>
                            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#3D3D3D] leading-tight italic">
                                Design méticuleux
                            </h2>
                            <h2 className="text-2xl md:text-3xl lg:text-4xl text-[#3D3D3D] font-bold uppercase mt-2">
                                ET INTELLIGENT
                            </h2>
                        </div>
                    </div>

                    {/* Benefits Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                        <div className="bg-[#3D3D3D] p-6 md:p-8 text-white">
                            <img 
                                src="https://images.unsplash.com/photo-1543198343-2c0ce108cc33?q=80&w=1631&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                                alt="Qualité"
                                className="w-full h-40 md:h-48 object-cover mb-4 md:mb-6"
                            />
                            <h3 className="text-base md:text-lg font-bold mb-2">Artisanat tunisien de pointe</h3>
                            <p className="text-xs md:text-sm text-gray-300">Conçu et perfectionné dans notre usine de Sfax avec des composants internationaux.</p>
                        </div>
                        <div className="bg-[#8B9A87] p-6 md:p-8 text-white">
                            <img 
                                src="https://scontent.fnbe1-2.fna.fbcdn.net/v/t39.30808-6/492557278_1348898896346208_6752670970780780812_n.jpg?_nc_cat=109&ccb=1-7&_nc_sid=833d8c&_nc_ohc=QjBbnOekqZoQ7kNvwHURoWI&_nc_oc=AdmTszHcTDy5DXsiA_DNDW8353S27nLe3P-lyXcBtPt0pc7cNsNC_Z2MwktU5QRYctmP7l_cleFQ3sHDsDpzTs61&_nc_zt=23&_nc_ht=scontent.fnbe1-2.fna&_nc_gid=ADp7EQhaD3-u8jjLvZPF-g&oh=00_AflJSlhfD-sf9x5CDe27wsB7yHsU9WZYDkMqIrETzIBCig&oe=694F152E"
                                alt="Qualité"
                                className="w-full h-40 md:h-48 object-cover mb-4 md:mb-6"
                            />
                            <h3 className="text-base md:text-lg font-bold mb-2">Qualité incomparable</h3>
                            <p className="text-xs md:text-sm text-white/80">Des tests de qualité qui dépassent les normes et certifications de l'industrie.</p>
                        </div>
                        <div className="bg-[#C5C0B8] p-6 md:p-8">
                            <img 
                                src="https://scontent.fnbe1-2.fna.fbcdn.net/v/t39.30808-6/523587504_1415718549664242_774969901783108605_n.jpg?_nc_cat=106&ccb=1-7&_nc_sid=833d8c&_nc_ohc=Ob0caJWxkI8Q7kNvwGZQdIl&_nc_oc=AdnyQtnWn-3T1sFodX1PhRx5gW48ND8_jBDJ5yl6zRaAy7rFy34iLUD0GFKiAjJjp2MoER9tJvqfNYhN0EoVECZ3&_nc_zt=23&_nc_ht=scontent.fnbe1-2.fna&_nc_gid=9FvjFv0uw3LkVe4XdtoA8Q&oh=00_AfkZmJ3dNpER3Emt7ExLUrloWzvL3PeqwAOhM72tPtanKQ&oe=694F1478"
                                alt="Qualité"
                                className="w-full h-40 md:h-48 object-cover mb-4 md:mb-6"
                            />
                            <h3 className="text-base md:text-lg font-bold mb-2 text-gray-800">Finitions et composants solides</h3>
                            <p className="text-xs md:text-sm text-gray-600">Dépassant toutes les normes en termes de durabilité, résistance thermique et qualité.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== FOOTER - Bélanger Style ========== */}
            <footer className="relative bg-[#C5C0B8] min-h-screen flex flex-col overflow-hidden z-50">
                {/* Large Background Text */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-30">
                    <h1 className="font-serif text-[35vw] md:text-[28vw] lg:text-[32vw] font-light text-[#3D3D3D]/10 leading-none select-none tracking-tight">
                        Modatex
                    </h1>
                </div>

                {/* Footer Content */}
                <div className="relative z-10 flex-1 flex flex-col justify-between max-w-7xl w-full mx-auto px-4 md:px-8 lg:px-12 py-12 md:py-16 lg:py-20">
                    {/* Top Section - Follow Us */}
                    <div className="pt-6 md:pt-8 lg:pt-16">
                        <div className="max-w-md">
                            <div className="mb-8 md:mb-10">
                                <h3 className="text-lg md:text-xl lg:text-2xl font-serif font-light text-[#3D3D3D] mb-2 tracking-tight">Suivez-nous</h3>
                                <div className="w-12 md:w-16 h-0.5 bg-[#3D3D3D]/30"></div>
                            </div>
                            <div className="grid grid-cols-2 gap-x-8 md:gap-x-12 gap-y-4 md:gap-y-5">
                                <a href="https://www.facebook.com/modatex.tunisie" className="group flex items-center gap-2 md:gap-3 text-sm md:text-[15px] lg:text-base text-[#3D3D3D]/70 hover:text-[#3D3D3D] transition-all duration-300">
                                    <svg className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                    </svg>
                                    <span>Facebook</span>
                                </a>
                                <a href="https://www.instagram.com/modatex_page_officielle/" className="group flex items-center gap-2 md:gap-3 text-sm md:text-[15px] lg:text-base text-[#3D3D3D]/70 hover:text-[#3D3D3D] transition-all duration-300">
                                    <svg className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                    </svg>
                                    <span>Instagram</span>
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Bar */}
                    <div className="border-t border-[#3D3D3D]/15 pt-6 md:pt-8 pb-3 md:pb-4">
                        <div className="flex flex-col lg:flex-row justify-between items-center gap-4 md:gap-6 lg:gap-4">
                            {/* Left Side - Fingerprint Icon */}
                            <div className="order-2 lg:order-1">
                                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#3D3D3D] flex items-center justify-center shadow-lg hover:bg-[#2D2D2D] transition-colors duration-300">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 md:w-7 md:h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                                    </svg>
                                </div>
                            </div>

                            {/* Center - Copyright */}
                            <div className="text-xs md:text-sm lg:text-[15px] text-[#3D3D3D]/70 order-1 lg:order-2">
                                ©2025 Tous droits réservés
                            </div>

                        </div>
                    </div>
                </div>
            </footer>
            </div>
        </div>
        </>
    );
};

export default Home;
